import React, { useEffect, useMemo, useRef, useState } from "react";
import "./resellerSharing.css";
import {
  CATALOGUE_SHARE_COUNTRIES,
  DEFAULT_SHARE_COUNTRY_ISO,
  findShareCountry,
  isPlausibleSharePhone,
  whatsappDigits,
} from "../../../components/catalogue/catalogueShareCountries";
import {
  createClientLink,
  requestMoreShares,
  revokeClientLink,
  type CreatedClientLink,
  type ResellerSummary,
} from "../../../components/catalogue/catalogueResellerApi";
import {
  clientInviteMessage,
  clientPricePreview,
  formatRupees,
  sharesLeftLabel,
  waDigits,
  whatsAppHref,
} from "./catalogueAudience";

export interface SharePiece {
  productUniqueId: string;
  name: string;
  /** The reseller's own price for this piece (already their tier). */
  price: number | null;
  image?: string | null;
}

type Props = {
  open: boolean;
  onClose: () => void;
  catalogueId: string;
  linkToken: string;
  sessionToken?: string;
  catalogueName?: string;
  totalPieces: number;
  selected: SharePiece[];
  /** A piece to preview prices with when sharing everything. */
  samplePiece?: SharePiece | null;
  summary: ResellerSummary | null;
  onSummaryChange: (next: ResellerSummary | null) => void;
  onRefreshSummary: () => void;
};

const MARKUPS = [10, 15, 20, 25];
const EXPIRY_DAYS = [3, 7, 15];
const EXIT_MS = 200;

type Tab = "new" | "clients";

const ResellerShareSheet: React.FC<Props> = ({
  open,
  onClose,
  catalogueId,
  linkToken,
  sessionToken,
  catalogueName,
  totalPieces,
  selected,
  samplePiece,
  summary,
  onSummaryChange,
  onRefreshSummary,
}) => {
  // Mounted while open or while the exit transition runs.
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (open) {
      setMounted(true);
      const id = window.requestAnimationFrame(() => setShown(true));
      return () => window.cancelAnimationFrame(id);
    }
    setShown(false);
    const t = window.setTimeout(() => setMounted(false), EXIT_MS);
    return () => window.clearTimeout(t);
  }, [open]);

  const [tab, setTab] = useState<Tab>("new");
  const [scope, setScope] = useState<"all" | "selected">(selected.length ? "selected" : "all");
  const [clientName, setClientName] = useState("");
  const [countryIso, setCountryIso] = useState(DEFAULT_SHARE_COUNTRY_ISO);
  const [phone, setPhone] = useState("");
  const [pricing, setPricing] = useState<"markup" | "perPiece">("markup");
  const [markup, setMarkup] = useState<number>(20);
  const [customMarkup, setCustomMarkup] = useState("");
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [expiryDays, setExpiryDays] = useState(15);
  const [access, setAccess] = useState<"EXCLUSIVE" | "OPEN">("EXCLUSIVE");
  // The access actually sent, so the done screen matches the link even if the toggle moved meanwhile.
  const [createdAccess, setCreatedAccess] = useState<"EXCLUSIVE" | "OPEN">("EXCLUSIVE");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState<CreatedClientLink | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);
  const [moreAsked, setMoreAsked] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setScope(selected.length ? "selected" : "all");
    setError("");
    if (!selected.length) setPricing("markup");
  }, [open, selected.length]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const focus = window.setTimeout(() => {
      if (tab === "new" && !created) nameRef.current?.focus({ preventScroll: true });
      else sheetRef.current?.focus({ preventScroll: true });
    }, 60);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
      window.clearTimeout(focus);
    };
  }, [open, onClose, tab, created]);

  const limit = summary?.forwardLimit ?? 0;
  const remaining = summary?.forwardsRemaining ?? 0;
  const noneLeft = Boolean(summary) && remaining <= 0;
  const effectiveMarkup = customMarkup.trim() ? Number(customMarkup) : markup;
  const markupValid = Number.isFinite(effectiveMarkup) && effectiveMarkup >= 0 && effectiveMarkup <= 500;
  const perPieceAvailable = scope === "selected" && selected.length > 0 && selected.length <= 50;
  const pieces = scope === "selected" ? selected : [];
  const preview = (scope === "selected" ? selected[0] : samplePiece) || null;
  const previewClient = clientPricePreview(preview?.price, markupValid ? effectiveMarkup : 0);

  const activeClients = useMemo(
    () => (summary?.clients || []).filter((c) => c.status === "ACTIVE").length,
    [summary]
  );

  const resetForm = () => {
    setClientName("");
    setPhone("");
    setOverrides({});
    setCustomMarkup("");
    setAccess("EXCLUSIVE");
    setCreated(null);
    setCopied(false);
    setError("");
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!clientName.trim() && access !== "OPEN") {
      setError("Enter your client's name.");
      return;
    }
    const sentAccess = access;
    // An open link needs no number; it is only for sending it on WhatsApp.
    const skipPhone = sentAccess === "OPEN" && !phone.trim();
    if (!skipPhone && !isPlausibleSharePhone(phone, countryIso)) {
      setError("Enter your client's mobile number.");
      return;
    }
    if (pricing === "markup" && !markupValid) {
      setError("Markup must be between 0% and 500%.");
      return;
    }
    if (scope === "selected" && selected.length === 0) {
      setError("Select pieces on the page first, or share all pieces.");
      return;
    }
    const priceOverrides: Record<string, number> = {};
    if (pricing === "perPiece") {
      for (const piece of pieces) {
        const raw = overrides[piece.productUniqueId];
        const n = raw != null && raw.trim() ? Number(raw.replace(/[,₹\s]/g, "")) : NaN;
        if (Number.isFinite(n) && n > 0) priceOverrides[piece.productUniqueId] = n;
      }
    }
    setBusy(true);
    setError("");
    try {
      const dial = findShareCountry(countryIso)?.dial || "91";
      const res = await createClientLink(catalogueId, linkToken, sessionToken, {
        clientName: clientName.trim(),
        clientPhone: phone,
        clientCountryCode: dial,
        productUniqueIds: scope === "selected" ? selected.map((p) => p.productUniqueId) : undefined,
        markupPercent: pricing === "markup" || Object.keys(priceOverrides).length < pieces.length
          ? (markupValid ? effectiveMarkup : 0)
          : undefined,
        priceOverrides: Object.keys(priceOverrides).length ? priceOverrides : undefined,
        expiryDays,
        accessMode: sentAccess,
      });
      if (res.success && res.data) {
        setCreatedAccess(sentAccess);
        setCreated(res.data);
        onRefreshSummary();
      } else {
        setError(res.message || "Could not create the link. Try again.");
        if (res.status === 409) onRefreshSummary();
      }
    } catch {
      setError("Network error. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const createdMessage = created
    ? clientInviteMessage({
        clientName: created.clientName,
        resellerName: created.resellerName,
        catalogueName: created.catalogueName || catalogueName,
        url: created.publicUrl,
        open: createdAccess === "OPEN",
      })
    : "";

  const copyLink = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(createdMessage);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  const revoke = async (id: string) => {
    if (confirmRevoke !== id) {
      setConfirmRevoke(id);
      return;
    }
    setConfirmRevoke(null);
    const res = await revokeClientLink(catalogueId, linkToken, sessionToken, id);
    if (res.success && res.data) onSummaryChange(res.data);
    else onRefreshSummary();
  };

  const askMore = async () => {
    const res = await requestMoreShares(catalogueId, linkToken, sessionToken);
    if (res.success) setMoreAsked(true);
  };

  if (!mounted) return null;

  return (
    <div className={`hkp-rs-layer${shown ? " is-open" : ""}`}>
      <div className="hkp-rs-scrim" onClick={onClose} aria-hidden="true" />
      <div
        ref={sheetRef}
        className="hkp-rs-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="hkp-rs-title"
        tabIndex={-1}
      >
        <div className="hkp-rs-grabber" aria-hidden="true" />
        <header className="hkp-rs-head">
          <div className="hkp-rs-head-text">
            <h2 id="hkp-rs-title">Share with my client</h2>
            {summary ? (
              <p className={`hkp-rs-quota${noneLeft ? " is-empty" : ""}`}>{sharesLeftLabel(remaining, limit)}</p>
            ) : null}
          </div>
          <button type="button" className="hkp-rs-icon-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <div className="hkp-rs-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "new"}
            className={tab === "new" ? "is-active" : ""}
            onClick={() => setTab("new")}
          >
            New share
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "clients"}
            className={tab === "clients" ? "is-active" : ""}
            onClick={() => setTab("clients")}
          >
            My clients{activeClients ? ` · ${activeClients}` : ""}
          </button>
        </div>

        <div className="hkp-rs-body">
          {tab === "new" && created ? (
            <div className="hkp-rs-done" role="status">
              <div className="hkp-rs-done-mark" aria-hidden="true">✓</div>
              <h3>Link for {created.clientName} is ready</h3>
              <p className="hkp-rs-muted">
                {createdAccess === "OPEN" ? "Anyone with the link can open it" : "It opens only with their number"}
                {created.expiresAt ? `, until ${shortDate(created.expiresAt)}` : ""}.
                {" "}{sharesLeftLabel(created.forwardsRemaining, created.forwardLimit)}.
              </p>
              <a
                className="hkp-rs-btn hkp-rs-btn-wa"
                href={whatsAppHref(
                  created.clientPhone ? whatsappDigits(countryIso, created.clientPhone) : "",
                  createdMessage
                )}
                target="_blank"
                rel="noopener noreferrer"
              >
                Send on WhatsApp
              </a>
              <button type="button" className="hkp-rs-btn hkp-rs-btn-quiet" onClick={copyLink}>
                {copied ? "Copied" : "Copy message"}
              </button>
              {created.forwardsRemaining > 0 ? (
                <button type="button" className="hkp-rs-linkbtn" onClick={resetForm}>
                  Share with another client
                </button>
              ) : null}
            </div>
          ) : null}

          {tab === "new" && !created && noneLeft ? (
            <div className="hkp-rs-empty">
              <h3>All {limit} shares are in use</h3>
              <p className="hkp-rs-muted">
                Revoke a client link you no longer need, or ask the studio for more shares.
              </p>
              <button type="button" className="hkp-rs-btn hkp-rs-btn-quiet" onClick={() => setTab("clients")}>
                See my clients
              </button>
              <button
                type="button"
                className="hkp-rs-btn hkp-rs-btn-primary"
                onClick={askMore}
                disabled={moreAsked}
              >
                {moreAsked ? "Studio has been asked" : "Request more shares"}
              </button>
            </div>
          ) : null}

          {tab === "new" && !created && !noneLeft ? (
            <form className="hkp-rs-form" onSubmit={submit} noValidate>
              <fieldset className="hkp-rs-field">
                <legend>Pieces</legend>
                <div className="hkp-rs-seg" role="radiogroup" aria-label="Pieces">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={scope === "all"}
                    className={scope === "all" ? "is-active" : ""}
                    onClick={() => {
                      setScope("all");
                      setPricing("markup");
                    }}
                  >
                    All pieces · {totalPieces}
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={scope === "selected"}
                    className={scope === "selected" ? "is-active" : ""}
                    disabled={selected.length === 0}
                    onClick={() => setScope("selected")}
                  >
                    Selected · {selected.length}
                  </button>
                </div>
                {selected.length === 0 ? (
                  <p className="hkp-rs-hint">To share only some pieces, close this and tap “Select pieces”.</p>
                ) : null}
              </fieldset>

              <div className="hkp-rs-field">
                <label htmlFor="hkp-rs-name">Client name{access === "OPEN" ? " (optional)" : ""}</label>
                <input
                  ref={nameRef}
                  id="hkp-rs-name"
                  className="hkp-rs-input"
                  value={clientName}
                  maxLength={120}
                  autoComplete="off"
                  enterKeyHint="next"
                  onChange={(e) => setClientName(e.target.value)}
                />
              </div>

              <div className="hkp-rs-field">
                <label htmlFor="hkp-rs-phone">
                  Client mobile{access === "OPEN" ? " (optional)" : ""}
                </label>
                <div className="hkp-rs-phone">
                  <select
                    aria-label="Country"
                    className="hkp-rs-input hkp-rs-country"
                    value={countryIso}
                    onChange={(e) => setCountryIso(e.target.value)}
                  >
                    {CATALOGUE_SHARE_COUNTRIES.map((c) => (
                      <option key={c.iso} value={c.iso}>
                        +{c.dial}
                      </option>
                    ))}
                  </select>
                  <input
                    id="hkp-rs-phone"
                    className="hkp-rs-input"
                    type="tel"
                    inputMode="tel"
                    autoComplete="off"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </div>
                <p className="hkp-rs-hint">
                  {access === "OPEN"
                    ? "We send the link to this number. Anyone they forward it to can browse."
                    : "The link opens only with this number."}
                </p>
              </div>

              <fieldset className="hkp-rs-field">
                <legend>Your client's price</legend>
                <div className="hkp-rs-seg" role="radiogroup" aria-label="Pricing">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={pricing === "markup"}
                    className={pricing === "markup" ? "is-active" : ""}
                    onClick={() => setPricing("markup")}
                  >
                    Add a markup
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={pricing === "perPiece"}
                    className={pricing === "perPiece" ? "is-active" : ""}
                    disabled={!perPieceAvailable}
                    onClick={() => setPricing("perPiece")}
                  >
                    Price each piece
                  </button>
                </div>

                {pricing === "markup" ? (
                  <>
                    <div className="hkp-rs-chips">
                      {MARKUPS.map((m) => (
                        <button
                          key={m}
                          type="button"
                          className={`hkp-rs-chip${!customMarkup && markup === m ? " is-active" : ""}`}
                          aria-pressed={!customMarkup && markup === m}
                          onClick={() => {
                            setMarkup(m);
                            setCustomMarkup("");
                          }}
                        >
                          {m}%
                        </button>
                      ))}
                      <label className="hkp-rs-chip-input">
                        <input
                          inputMode="decimal"
                          placeholder="Other"
                          aria-label="Other markup percent"
                          value={customMarkup}
                          onChange={(e) => setCustomMarkup(e.target.value.replace(/[^\d.]/g, ""))}
                        />
                        <span aria-hidden="true">%</span>
                      </label>
                    </div>
                    {preview?.price && previewClient ? (
                      <p className="hkp-rs-preview">
                        <span className="hkp-rs-preview-from">{formatRupees(preview.price)}</span>
                        <span aria-hidden="true"> → </span>
                        <strong>{formatRupees(previewClient)}</strong>
                        <span className="hkp-rs-muted"> for your client</span>
                      </p>
                    ) : (
                      <p className="hkp-rs-hint">Pieces without a price show “Ask for price”.</p>
                    )}
                  </>
                ) : (
                  <ul className="hkp-rs-pieces">
                    {pieces.map((piece) => {
                      const suggested = clientPricePreview(piece.price, markupValid ? effectiveMarkup : 0);
                      return (
                        <li key={piece.productUniqueId}>
                          <span
                            className="hkp-rs-thumb"
                            style={piece.image ? { backgroundImage: `url(${piece.image})` } : undefined}
                            aria-hidden="true"
                          />
                          <span className="hkp-rs-piece-text">
                            <span className="hkp-rs-piece-name" title={piece.name}>
                              {piece.name}
                            </span>
                            <span className="hkp-rs-muted">
                              Yours {piece.price ? formatRupees(piece.price) : "—"}
                            </span>
                          </span>
                          <label className="hkp-rs-piece-price">
                            <span aria-hidden="true">₹</span>
                            <input
                              inputMode="decimal"
                              aria-label={`Client price for ${piece.name}`}
                              placeholder={suggested ? String(suggested) : ""}
                              value={overrides[piece.productUniqueId] || ""}
                              onChange={(e) =>
                                setOverrides((o) => ({
                                  ...o,
                                  [piece.productUniqueId]: e.target.value.replace(/[^\d.]/g, ""),
                                }))
                              }
                            />
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </fieldset>

              <fieldset className="hkp-rs-field">
                <legend>Who can open it</legend>
                <div className="hkp-rs-seg" role="radiogroup" aria-label="Who can open it">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={access === "EXCLUSIVE"}
                    className={access === "EXCLUSIVE" ? "is-active" : ""}
                    disabled={busy}
                    onClick={() => setAccess("EXCLUSIVE")}
                  >
                    Only this client
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={access === "OPEN"}
                    className={access === "OPEN" ? "is-active" : ""}
                    disabled={busy}
                    onClick={() => setAccess("OPEN")}
                  >
                    Anyone with the link
                  </button>
                </div>
                <p className="hkp-rs-hint">
                  {access === "OPEN" ? "Opens without a code. You won't see who else opens it." : "Opens with a code sent to their mobile."}
                </p>
              </fieldset>

              <fieldset className="hkp-rs-field">
                <legend>Link open for</legend>
                <div className="hkp-rs-seg" role="radiogroup" aria-label="Link open for">
                  {EXPIRY_DAYS.map((d) => (
                    <button
                      key={d}
                      type="button"
                      role="radio"
                      aria-checked={expiryDays === d}
                      className={expiryDays === d ? "is-active" : ""}
                      onClick={() => setExpiryDays(d)}
                    >
                      {d} days
                    </button>
                  ))}
                </div>
                {summary?.linkExpiresAt ? (
                  <p className="hkp-rs-hint">Never after your own link ends ({shortDate(summary.linkExpiresAt)}).</p>
                ) : null}
              </fieldset>

              {error ? (
                <p className="hkp-rs-error" role="alert">
                  {error}
                </p>
              ) : null}

              <div className="hkp-rs-submit">
                <button type="submit" className="hkp-rs-btn hkp-rs-btn-primary" disabled={busy}>
                  {busy ? "Creating link…" : "Create client link"}
                </button>
              </div>
            </form>
          ) : null}

          {tab === "clients" ? (
            <div className="hkp-rs-clients">
              {!summary || summary.clients.length === 0 ? (
                <p className="hkp-rs-muted hkp-rs-empty-line">No client links yet.</p>
              ) : (
                <ul>
                  {summary.clients.map((c) => (
                    <li key={c.linkId} className={`hkp-rs-client is-${c.status.toLowerCase()}`}>
                      <div className="hkp-rs-client-main">
                        <span className="hkp-rs-client-name" title={c.clientName}>
                          {c.clientName}
                        </span>
                        <span className="hkp-rs-muted hkp-rs-client-meta">
                          {c.clientPhone || c.clientPhoneMasked || "No mobile"}
                          {" · "}
                          {statusLabel(c.status, c.expiresAt)}
                          {c.accessMode === "OPEN" ? " · Open" : ""}
                        </span>
                        <span className="hkp-rs-client-stats">
                          {plural(c.views || 0, "view")} · {c.likes || 0} ♥
                        </span>
                      </div>
                      {c.status === "ACTIVE" ? (
                        <div className="hkp-rs-client-actions">
                          {c.publicUrl ? (
                            <a
                              className="hkp-rs-mini"
                              href={whatsAppHref(
                                waDigits(c.clientPhone, c.clientCountryCode),
                                clientInviteMessage({
                                  clientName: c.clientName,
                                  resellerName: summary.resellerName,
                                  catalogueName,
                                  url: c.publicUrl,
                                  open: c.accessMode === "OPEN",
                                })
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Resend
                            </a>
                          ) : null}
                          <button
                            type="button"
                            className={`hkp-rs-mini${confirmRevoke === c.linkId ? " is-danger" : ""}`}
                            onClick={() => void revoke(c.linkId)}
                            onBlur={() => setConfirmRevoke((id) => (id === c.linkId ? null : id))}
                          >
                            {confirmRevoke === c.linkId ? "Tap to revoke" : "Revoke"}
                          </button>
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
              {noneLeft ? (
                <button
                  type="button"
                  className="hkp-rs-btn hkp-rs-btn-quiet"
                  onClick={askMore}
                  disabled={moreAsked}
                >
                  {moreAsked ? "Studio has been asked" : "Request more shares"}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

function shortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

function plural(n: number, word: string): string {
  return `${new Intl.NumberFormat("en-IN").format(n)} ${word}${n === 1 ? "" : "s"}`;
}

function statusLabel(status: string, expiresAt?: string): string {
  if (status === "REVOKED") return "Revoked";
  if (status === "EXPIRED") return "Expired";
  return expiresAt ? `Open until ${shortDate(expiresAt)}` : "Open";
}

export default ResellerShareSheet;
